import React, { useState, useEffect, useRef, useCallback } from "react";
import { useEditor } from "../EditorContext";
import { ChevronLeft, PanelLeft } from "lucide-react";
import CodeSection from "./CodeSection";
import ComponentCodeViewer from "./ComponentCodeViewer";
import ComponentHistoryList from "./ComponentHistoryList";
import { useCodePrefetch } from "./useCodePrefetch";

function CodeEditorPanel({
    components = [],
    dashboardSection,
    onComponentUpdated,
    onBackToMenu,
    onCreateFile,
    onCreateFolder,
    onDeleteFile,
    onRenameFile,
    hasCodeEditorAccess = true, // Default to true for backwards compatibility
    isEnviromentLocked = false,
}) {
    const {
        openFiles,
        activeFileId,
        openFile,
        closeFile,
        setActiveFileId,
        syncComponents,
        fileStates,
    } = useEditor();
    const { ensureCode, invalidateCode } = useCodePrefetch(components);

    // Detect if we're on mobile on mount
    const [isMobile] = useState(() => {
        if (typeof window !== "undefined") {
            return window.innerWidth < 768;
        }
        return false;
    });

    // Sync components with context
    useEffect(() => {
        if (components.length > 0) {
            syncComponents(components);
        }
    }, [components, syncComponents]);

    const selectedFile = React.useMemo(() => {
        if (!activeFileId) return null;
        return (
            components.find((c) => c.id === activeFileId) ||
            openFiles.find((f) => f.id === activeFileId)
        );
    }, [activeFileId, components, openFiles]);

    const [highlightLine, setHighlightLine] = useState(null);
    const [showFileSearch, setShowFileSearch] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [showMobileEditor, setShowMobileEditor] = useState(false); // For mobile: toggle between file list and editor
    const saveHandlerRef = useRef(null);
    const codeSectionRef = useRef(null);

    // Wrap onComponentUpdated to invalidate prefetch cache
    const handleComponentUpdated = useCallback(() => {
        if (activeFileId) {
            invalidateCode(activeFileId);
        }
        if (onComponentUpdated) {
            onComponentUpdated();
        }
    }, [activeFileId, invalidateCode, onComponentUpdated]);

    // Handle file selection
    const handleFileSelect = (component, lineNumber = null) => {
        openFile(component);
        ensureCode(component);
        if (lineNumber) {
            setHighlightLine(lineNumber);
        }
        if (isMobile) {
            setShowMobileEditor(true);
        }
    };

    // Handle opening a file (add to open files)
    const handleFileOpen = (component) => {
        openFile(component);
        ensureCode(component);
    };

    // Handle closing a file
    const handleCloseFile = useCallback(
        (component) => {
            closeFile(component.id);

            // On mobile, go back to file list when no files are open (handled in context/effect mostly, but here for UI toggle)
            if (
                isMobile &&
                openFiles.length <= 1 &&
                activeFileId === component.id
            ) {
                setShowMobileEditor(false);
            }
        },
        [closeFile, isMobile, openFiles, activeFileId]
    );

    // Handle back to file list on mobile
    const handleBackToFileList = () => {
        setShowMobileEditor(false);
    };

    // Handle tab click
    const handleTabClick = (component) => {
        setActiveFileId(component.id);
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Cmd/Ctrl + S: Save
            if ((e.metaKey || e.ctrlKey) && e.key === "s") {
                e.preventDefault();
                e.stopPropagation();
                if (saveHandlerRef.current) {
                    saveHandlerRef.current();
                }
                return;
            }

            // Cmd/Ctrl + W: Close current file
            if ((e.metaKey || e.ctrlKey) && e.key === "w") {
                e.preventDefault();
                e.stopPropagation();
                if (selectedFile) {
                    handleCloseFile(selectedFile);
                }
                return;
            }

            // Cmd/Ctrl + P: Quick file search
            if ((e.metaKey || e.ctrlKey) && e.key === "p") {
                e.preventDefault();
                e.stopPropagation();
                setShowFileSearch(true);
                // Focus on search input in CodeSection
                if (codeSectionRef.current) {
                    codeSectionRef.current.focusSearch();
                }
                return;
            }
        };

        window.addEventListener("keydown", handleKeyDown, { capture: true });
        return () =>
            window.removeEventListener("keydown", handleKeyDown, {
                capture: true,
            });
    }, [selectedFile, handleCloseFile]);

    return (
        <div className="h-full w-full flex flex-col min-h-0 overflow-hidden">
            {/* Mobile Layout - Show either file list OR editor */}
            {isMobile ? (
                <div className="flex-1 flex flex-col overflow-hidden min-h-0 h-full w-full">
                    {!showMobileEditor ? (
                        // Show file list on mobile - full width and height
                        <div className="flex-1 h-full w-full overflow-hidden">
                            <CodeSection
                                ref={codeSectionRef}
                                components={components}
                                onFileSelect={handleFileSelect}
                                selectedFile={selectedFile}
                                openFiles={openFiles}
                                onCloseFile={handleCloseFile}
                                onFileOpen={handleFileOpen}
                                showFileSearch={showFileSearch}
                                onFileSearchClose={() =>
                                    setShowFileSearch(false)
                                }
                                dashboardSection={dashboardSection}
                                onBackToMenu={onBackToMenu}
                                onCloseSidebar={() =>
                                    setIsSidebarCollapsed(true)
                                }
                                onCreateFile={onCreateFile}
                                onCreateFolder={onCreateFolder}
                                onDeleteFile={onDeleteFile}
                                onRenameFile={onRenameFile}
                                ensureCode={ensureCode}
                            />
                        </div>
                    ) : (
                        // Show editor on mobile
                        <div className="flex-1 flex flex-col overflow-hidden min-h-0 h-full">
                            {/* Mobile Editor Header with Back Button */}
                            <div className="border-b bg-white flex items-center px-2 py-2 flex-shrink-0">
                                <button
                                    onClick={handleBackToFileList}
                                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    <span>Files</span>
                                </button>
                                {selectedFile && (
                                    <div className="ml-2 flex-1 truncate text-sm font-medium text-gray-900">
                                        {selectedFile.name}
                                    </div>
                                )}
                            </div>

                            {/* Code Viewer - Pass null dashboardSection to force showing editor */}
                            <div className="flex-1 min-h-0 overflow-hidden h-full">
                                <ComponentCodeViewer
                                    selectedComponent={selectedFile}
                                    dashboardSection={null}
                                    onComponentUpdated={handleComponentUpdated}
                                    highlightLine={highlightLine}
                                    hasCodeEditorAccess={hasCodeEditorAccess}
                                    isEnviromentLocked={isEnviromentLocked}
                                    onSaveHandlerReady={(handler) => {
                                        saveHandlerRef.current = handler;
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                // Desktop Layout - Show both side by side
                <div className="flex-1 flex overflow-hidden min-h-0">
                    {/* Code Section (Files & Search) */}
                    {!isSidebarCollapsed && (
                        <div className="flex flex-col w-64 border-r bg-gray-50 flex-shrink-0">
                            <div className="flex-1 overflow-hidden">
                                <CodeSection
                                    ref={codeSectionRef}
                                    components={components}
                                    onFileSelect={handleFileSelect}
                                    selectedFile={selectedFile}
                                    openFiles={openFiles}
                                    onCloseFile={handleCloseFile}
                                    onFileOpen={handleFileOpen}
                                    showFileSearch={showFileSearch}
                                    onFileSearchClose={() =>
                                        setShowFileSearch(false)
                                    }
                                    dashboardSection={dashboardSection}
                                    onBackToMenu={onBackToMenu}
                                    onCloseSidebar={() =>
                                        setIsSidebarCollapsed(true)
                                    }
                                    onCreateFile={onCreateFile}
                                    onCreateFolder={onCreateFolder}
                                    onDeleteFile={onDeleteFile}
                                    onRenameFile={onRenameFile}
                                />
                            </div>
                            {selectedFile && (
                                <div className="flex-shrink-0 max-h-[40%] overflow-hidden border-t">
                                    <ComponentHistoryList
                                        componentId={selectedFile.id}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Reopen Sidebar Button */}
                    {isSidebarCollapsed && (
                        <button
                            onClick={() => setIsSidebarCollapsed(false)}
                            className="w-10 border-r bg-gray-50 hover:bg-gray-100 flex items-center justify-center h-full"
                            title="Show Files"
                            aria-label="Show Files"
                        >
                            <PanelLeft className="w-4 h-4 text-gray-500" />
                        </button>
                    )}

                    {/* Main Editor Area */}
                    <div
                        className="flex flex-col overflow-hidden min-h-0 w-full"
                        style={{ flexGrow: 1, minWidth: 0 }}
                    >
                        {/* File Tabs */}
                        {openFiles.length > 0 && (
                            <div className="border-b bg-white flex-shrink-0">
                                <div className="flex overflow-x-auto scrollbar-thin">
                                    {openFiles.map((file) => {
                                        const hasUnsavedChanges =
                                            fileStates[file.id]?.hasChanges;
                                        return (
                                            <div
                                                key={file.id}
                                                className={`flex items-center gap-2 px-3 py-2 text-sm border-r cursor-pointer min-w-32 max-w-48 ${
                                                    selectedFile?.id === file.id
                                                        ? "bg-white text-blue-600 border-b-2 border-blue-600"
                                                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                                                }`}
                                                onClick={() =>
                                                    handleTabClick(file)
                                                }
                                            >
                                                <span className="truncate flex-1 flex items-center gap-1">
                                                    {file.name}
                                                    {hasUnsavedChanges && (
                                                        <span
                                                            className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0"
                                                            title="Unsaved changes"
                                                        />
                                                    )}
                                                </span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCloseFile(file);
                                                    }}
                                                    className="hover:bg-gray-200 rounded p-1 flex-shrink-0 w-5 h-5 flex items-center justify-center"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Code Viewer */}
                        <div className="flex-1 min-h-0 overflow-hidden">
                            <ComponentCodeViewer
                                selectedComponent={selectedFile}
                                dashboardSection={dashboardSection}
                                onComponentUpdated={handleComponentUpdated}
                                highlightLine={highlightLine}
                                hasCodeEditorAccess={hasCodeEditorAccess}
                                isEnviromentLocked={isEnviromentLocked}
                                onSaveHandlerReady={(handler) => {
                                    saveHandlerRef.current = handler;
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CodeEditorPanel;
